'use client'

import { useMutation } from '@tanstack/react-query'
import { useCallback } from 'react'
import { toast } from 'react-toastify'
import { CUSTOMER_TYPE } from '@/constants/customer'
import {
    changePasswordService,
    updateCompanyCustomerService,
    updateCustomerService,
} from '@/services/domain/customer/CustomerService'
import { useAuthStore } from '@/stores/useAuthStore'
import type {
    IChangePasswordRequest,
    ICustomer,
    IUpdateCompanyCustomerRequest,
    IUpdateCustomerRequest,
} from '~/schemas/customer/customer'

const PERSON_TYPES: readonly string[] = [CUSTOMER_TYPE.PERSON, CUSTOMER_TYPE.PERSON_SOCIAL_LOGIN]

/** Tipos do celular: o que a API grava hoje (`CellPhone`) e o de cadastros antigos (`Celular`). */
const CELL_PHONE_TYPES: readonly string[] = ['CellPhone', 'Celular']

/**
 * Celular do cliente para os formulários (DDD e número juntos, sem máscara): o telefone `CellPhone`, senão o primeiro;
 * sem telefone, vazio.
 */
export function getCustomerCellPhone(customer: Pick<ICustomer, 'telephones'> | undefined): string {
    const telephones = customer?.telephones ?? []
    const phone = telephones.find((item) => CELL_PHONE_TYPES.includes(item.type)) ?? telephones[0]
    return phone ? `${phone.prefix}${phone.number}` : ''
}

// ===== CLIENTE LOGADO =====

/**
 * Cliente logado, lido da useAuthStore (gravada pelo login e pelos services deste domínio). Não busca na API.
 * `isCompany` segue a regra da tela de perfil: tudo o que não é PF é tratado como PJ.
 */
export function useCustomer() {
    const customer = useAuthStore((state) => state.data?.customer)

    return {
        customer,
        isLoggedIn: Boolean(customer),
        isCompany: Boolean(customer) && !PERSON_TYPES.includes(customer?.type ?? ''),
    }
}

// ===== EDITAR DADOS =====

const UPDATED_MESSAGE = 'Dados atualizados com sucesso!'

/** Edição dos dados PF e PJ. O customer devolvido vai para a store no service; o erro chega a quem chamou. */
export function useUpdateCustomer() {
    const updateMutation = useMutation({
        mutationFn: (data: IUpdateCustomerRequest) => updateCustomerService(data),
        onSuccess: () => {
            toast.success(UPDATED_MESSAGE, { autoClose: 2000 })
        },
    })

    const updateCompanyMutation = useMutation({
        mutationFn: (data: IUpdateCompanyCustomerRequest) => updateCompanyCustomerService(data),
        onSuccess: () => {
            toast.success(UPDATED_MESSAGE, { autoClose: 2000 })
        },
    })

    const updateCustomer = useCallback(
        (data: IUpdateCustomerRequest) => updateMutation.mutateAsync(data),
        [updateMutation.mutateAsync],
    )
    const updateCompanyCustomer = useCallback(
        (data: IUpdateCompanyCustomerRequest) => updateCompanyMutation.mutateAsync(data),
        [updateCompanyMutation.mutateAsync],
    )

    return {
        updateCustomer,
        updateCompanyCustomer,
        isUpdating: updateMutation.isPending || updateCompanyMutation.isPending,
    }
}

// ===== TROCAR SENHA =====

export function useChangePassword() {
    const mutation = useMutation({
        mutationFn: (data: Omit<IChangePasswordRequest, 'email'>) => changePasswordService(data),
        onSuccess: () => {
            toast.success('Senha alterada com sucesso!', { autoClose: 2000 })
        },
    })

    const changePassword = useCallback(
        (data: Omit<IChangePasswordRequest, 'email'>) => mutation.mutateAsync(data),
        [mutation.mutateAsync],
    )

    return { changePassword, isChanging: mutation.isPending }
}
